/* eslint-disable no-param-reassign */
import Dynamic from 'next/dynamic';
import { useEffect, memo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from '@auth0/nextjs-auth0/client';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReactHtmlParser from 'react-html-parser';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { UpdateAccommodation } from 'store/actions/schoolDetailActions';

import {
  unsetAccommodationGroups,
  unsetAccommodations,
  toggleBedroomType,
} from 'utils/accommodationUtils';
import { sortBySelected } from 'utils/arrayUtils';
import { shouldUnlockPrice } from 'utils/lead';
import { formatDate } from 'utils/date';

export const PriceComponent = Dynamic(
  () => import('components/PriceComponent'),
  { ssr: false }
);

export const SelectedButton = Dynamic(
  () => import('components/Buttons/SelectedButtonComponent'),
  { ssr: false }
);

export const DescriptionButton = Dynamic(
  () => import('components/Buttons/DescriptionButtonComponent'),
  { ssr: false }
);

export const UnlockEstimateComponent = Dynamic(
  () => import('components/UnlockEstimateComponent'),
  { ssr: false }
);

function AccommodationsComponent() {
  const dispatch = useDispatch();
  const { accommodationGroups, needAccommodation } = useSelector(
    (store) => store.schoolDetailReducer
  );
  const [seeMore, setSeeMore] = useState(3);
  const handleSeeMore = () => setSeeMore(accommodationGroups?.length);
  const { user } = useUser();
  const { lead } = useSelector((store) => store.studentReducer);

  const toggleAccommodationGroupDescription = (id) => {
    accommodationGroups.filter((group) => {
      if (group.id === id) group.aboutToggle = !group.aboutToggle;
    });

    UpdateAccommodation(dispatch, accommodationGroups);
  };

  useEffect(() => {
    if (!needAccommodation) unsetAccommodationGroups(accommodationGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needAccommodation]);

  useEffect(() => {
    accommodationGroups[0].aboutToggle = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accommodationGroups[0]]);

  const getValue = (group) => {
    if (group.selected === undefined) return '';

    const accommodation = group.accommodations.filter(
      (a) => a.selected === true
    )[0];

    const priceRange = accommodation.priceRanges.filter(
      (pr) => pr.selected === true
    );

    return priceRange[0].exchanged;
  };

  const getValueWithDiscount = (group) => {
    if (group.selected === undefined) return '';

    const accommodation = group.accommodations.filter(
      (a) => a.selected === true
    )[0];

    const priceRange = accommodation.priceRanges.filter(
      (pr) => pr.selected === true
    );

    return priceRange[0].totalExchanged;
  };

  const toggleMeal = (group, accommodation) => {
    unsetAccommodations(group.accommodations);
    accommodation.selected = true;
    UpdateAccommodation(dispatch, accommodationGroups);
  };

  const toggleBedroom = (group, priceRange) => {
    toggleBedroomType(group, priceRange.bedroom);

    UpdateAccommodation(dispatch, accommodationGroups);
  };

  return (
    <Collapse in={!!needAccommodation}>
      <MuiAlert
        variant="outlined"
        severity="info"
        sx={{
          border: '1px solid rgba(200, 200, 200, 0.87)',
          color: 'rgba(100, 100, 100, 0.87)',
          marginTop: '8px',
        }}
      >
        Acomodação sujeita a disponibilidade. As acomodações começam no domingo
        e terminam no sábado, caso precise chegar antes ou sair depois, altere
        as datas acima.
      </MuiAlert>

      <List>
        {needAccommodation &&
          accommodationGroups
            .slice(0, seeMore)
            .sort(sortBySelected)
            .map((group) => (
              <ListItem disableGutters key={`acgroup-${group.id}`}>
                <Box
                  textAlign="left"
                  flexGrow={1}
                  bgcolor="background.paper"
                  border={1}
                  borderColor={group.selected ? 'text.primary' : '#C3C2C2E8'}
                  borderRadius="4px"
                  boxShadow={group.selected === true ? 5 : 0}
                >
                  <Box px={2} py={1} borderRadius="10px 10px 0 0">
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Box p={1}>
                          <Typography
                            component="h4"
                            sx={{ letterSpacing: 0, fontWeight: 900 }}
                            variant="h5"
                          >
                            {group.name}
                          </Typography>

                          <Box my={1}>
                            <Divider />
                          </Box>

                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Typography variant="body1" fontWeight="bold">
                                Alimentação:
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={9}>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  aria-label="alimentacao"
                                  name="alimentacao"
                                  row
                                >
                                  {group.accommodations.map((accommodation) => (
                                    <FormControlLabel
                                      key={`accomod-${accommodation.id}`}
                                      value={accommodation.meal}
                                      control={
                                        <Radio
                                          sx={{ padding: '4px' }}
                                          checked={accommodation?.selected}
                                        />
                                      }
                                      onClick={() =>
                                        toggleMeal(group, accommodation)
                                      }
                                      label={
                                        <Typography variant="body2">
                                          {accommodation.mealName}
                                        </Typography>
                                      }
                                    />
                                  ))}
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                              <Typography variant="body1" fontWeight="bold">
                                Quarto:
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={9}>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  aria-label="acomodacao"
                                  name="acomodacao"
                                  row
                                >
                                  {group.accommodations.map(
                                    (accommodation, index) =>
                                      accommodation.selected &&
                                      accommodation.priceRanges.map(
                                        (priceRange) => (
                                          <FormControlLabel
                                            key={`priceRange-${priceRange.id}`}
                                            value={String(
                                              priceRange.id + index
                                            )}
                                            control={
                                              <Radio
                                                sx={{ padding: '4px' }}
                                                checked={priceRange.selected}
                                              />
                                            }
                                            onClick={() =>
                                              toggleBedroom(group, priceRange)
                                            }
                                            label={
                                              <Typography variant="body2">
                                                {priceRange.bedroomName}
                                              </Typography>
                                            }
                                          />
                                        )
                                      )
                                  )}
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        {group.accommodations.map(
                          (accommodation) =>
                            accommodation.selected && (
                              <Collapse
                                key={`accomod-desc-${accommodation.id}`}
                                in={!!group?.aboutToggle}
                              >
                                <Divider />

                                <Typography
                                  component="div"
                                  sx={{ marginTop: '8px' }}
                                  variant="body2"
                                >
                                  {ReactHtmlParser(accommodation.description)}
                                </Typography>

                                {accommodation.priceRanges.map(
                                  (pr) =>
                                    pr.selected &&
                                    pr.supplements.map((supplement) => (
                                      <Grid
                                        container
                                        key={`supplement-${supplement.id}`}
                                      >
                                        <Grid
                                          item
                                          xs={12}
                                          sx={{
                                            filter: shouldUnlockPrice(
                                              lead,
                                              user
                                            )
                                              ? 'none'
                                              : 'blur(6px)',
                                            userSelect: shouldUnlockPrice(
                                              lead,
                                              user
                                            )
                                              ? 'initial'
                                              : 'none',
                                          }}
                                        >
                                          <Box py={1}>
                                            <MuiAlert
                                              variant="outlined"
                                              severity="warning"
                                              icon={
                                                <TrendingUpIcon fontSize="inherit" />
                                              }
                                            >
                                              {supplement.description &&
                                                supplement.description !==
                                                  '' && (
                                                  <>
                                                    <Box
                                                      fontWeight={600}
                                                      component="span"
                                                    >
                                                      {ReactHtmlParser(
                                                        supplement.description
                                                      )}
                                                      :
                                                    </Box>
                                                    &nbsp;
                                                  </>
                                                )}
                                              Entre{' '}
                                              {formatDate(
                                                supplement?.startDate,
                                                'day-and-month'
                                              )}{' '}
                                              e{' '}
                                              {formatDate(
                                                supplement.endDate,
                                                'day-and-month'
                                              )}
                                              , há um acréscimo de{' '}
                                              <PriceComponent
                                                value={
                                                  supplement.price *
                                                  pr.exchangeRateUsed
                                                }
                                              />{' '}
                                              por semana.
                                            </MuiAlert>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    ))
                                )}
                              </Collapse>
                            )
                        )}
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  <Box
                    p={2}
                    bgcolor="background.secondary.main"
                    borderRadius="0 0 4px 4px"
                  >
                    <Grid container spacing={1} alignItems="center">
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={7}
                        sx={{
                          filter: shouldUnlockPrice(lead, user)
                            ? 'none'
                            : 'blur(6px)',
                          userSelect: shouldUnlockPrice(lead, user)
                            ? 'initial'
                            : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h5" fontWeight="bold">
                            <PriceComponent
                              value={getValueWithDiscount(group)}
                            />
                          </Typography>

                          {getValue(group) > getValueWithDiscount(group) && (
                            <Typography
                              variant="h5"
                              sx={{
                                color: '#8a8a8a',
                                fontWeight: 'bold',
                                marginLeft: '16px',
                                textDecoration: 'line-through',
                              }}
                            >
                              <PriceComponent value={getValue(group)} />
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={5} sm={3} md={2}>
                        <DescriptionButton
                          key={`group-about-${group.id}`}
                          size="large"
                          fullWidth
                          onClick={() =>
                            toggleAccommodationGroupDescription(group.id)
                          }
                        >
                          <Box display="flex" alignItems="center" pl={0.4}>
                            {group.aboutToggle ? (
                              <RemoveIcon fontSize="small" />
                            ) : (
                              <AddIcon fontSize="small" />
                            )}
                            &nbsp;Detalhes
                          </Box>
                        </DescriptionButton>
                      </Grid>

                      {shouldUnlockPrice(lead, user) ? (
                        <Grid item xs={7} sm={3}>
                          {group.selected ? (
                            <SelectedButton
                              disableElevation
                              disableFocusRipple
                              disableRipple
                              fullWidth
                              size="large"
                              variant="contained"
                            >
                              Selecionado
                            </SelectedButton>
                          ) : (
                            <Button
                              size="large"
                              variant="contained"
                              color="secondary"
                              fullWidth
                              onClick={() => {
                                unsetAccommodationGroups(accommodationGroups);
                                // eslint-disable-next-line no-param-reassign
                                group.selected = true;
                                group.aboutToggle = true;

                                UpdateAccommodation(
                                  dispatch,
                                  accommodationGroups
                                );
                              }}
                            >
                              Selecionar
                            </Button>
                          )}
                        </Grid>
                      ) : (
                        <Grid item xs={7} sm={3}>
                          <Box sx={{ '& > button': { minHeight: '42px' } }}>
                            <UnlockEstimateComponent />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>
              </ListItem>
            ))}
      </List>

      {seeMore !== accommodationGroups?.length &&
        accommodationGroups?.length > 3 && (
          <Box my={1} textAlign="center">
            <Button onClick={handleSeeMore} variant="text">
              Ver mais
            </Button>
          </Box>
        )}
    </Collapse>
  );
}

export default memo(AccommodationsComponent);
